import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type {
  Expense,
  InventoryBatch,
  InternalWarehouse,
  PosBranch,
  PosSale,
  PosShift,
  PurchaseOrder,
  PurchaseOrderDetail,
  Supplier,
  SupplierBill,
} from '@terra/api-client';
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
} from '@terra/ui';

import { api } from '../lib/api';

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: string[];
  active: string;
  onChange: (t: string) => void;
}) {
  return (
    <div className="border-border/60 mb-6 flex flex-wrap gap-2 border-b pb-2">
      {tabs.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => onChange(t)}
          className={`rounded-xl px-4 py-2 text-[13px] font-semibold transition-all ${
            active === t
              ? 'bg-ink text-white shadow-sm'
              : 'text-ink-subtle hover:bg-surface-subtle/50 hover:text-ink'
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ERP  (Phase 2.4 — suppliers, POs, inventory, bills, expenses)
// ══════════════════════════════════════════════════════════════════

const PO_STATUS_TONE: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'success',
  PARTIAL_RECEIVED: 'warning',
  RECEIVED: 'success',
  CANCELLED: 'danger',
  CLOSED: 'neutral',
};
const BILL_STATUS_TONE: Record<string, BadgeTone> = {
  PENDING: 'warning',
  APPROVED: 'success',
  PAID: 'success',
  PARTIALLY_PAID: 'warning',
  DISPUTED: 'danger',
  CANCELLED: 'neutral',
};
const EXPENSE_STATUS_TONE: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'success',
  PAID: 'success',
  REJECTED: 'danger',
};
const EXPENSE_CATEGORIES = [
  'OFFICE',
  'UTILITIES',
  'RENT',
  'SOFTWARE',
  'MARKETING',
  'TRAVEL',
  'PROFESSIONAL_FEES',
  'REPAIRS',
  'OTHER',
] as const;

function SuppliersTab() {
  const qc = useQueryClient();
  const suppliers = useQuery({
    queryKey: ['erp-suppliers'],
    queryFn: () => api.erp.listSuppliers(),
  });
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    gstin: '',
    stateCode: '',
    paymentTermsDays: '30',
  });

  const create = useMutation({
    mutationFn: () =>
      api.erp.createSupplier({
        name: form.name.trim(),
        ...(form.email ? { email: form.email } : {}),
        ...(form.phone ? { phone: form.phone } : {}),
        ...(form.gstin ? { gstin: form.gstin.toUpperCase() } : {}),
        ...(form.stateCode ? { stateCode: form.stateCode } : {}),
        paymentTermsDays: Number(form.paymentTermsDays),
      }),
    onSuccess: () => {
      setForm({ name: '', email: '', phone: '', gstin: '', stateCode: '', paymentTermsDays: '30' });
      void qc.invalidateQueries({ queryKey: ['erp-suppliers'] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 border-b px-5 py-4">
          <h3 className="text-ink text-[14px] font-bold">New supplier</h3>
        </div>
        <div className="p-5">
          <form
            className="grid gap-4 sm:grid-cols-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (form.name.length >= 2) create.mutate();
            }}
          >
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
            <Input
              label="Email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
            <Input
              label="Phone"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
            <Input
              label="GSTIN"
              value={form.gstin}
              onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))}
            />
            <Input
              label="State code"
              value={form.stateCode}
              onChange={(e) => setForm((f) => ({ ...f, stateCode: e.target.value }))}
              hint="2 chars, e.g. KA"
            />
            <Input
              label="Payment terms (days)"
              type="number"
              value={form.paymentTermsDays}
              onChange={(e) => setForm((f) => ({ ...f, paymentTermsDays: e.target.value }))}
            />
            <div className="border-border/60 flex justify-end border-t pt-4 sm:col-span-3">
              <button
                type="submit"
                disabled={create.isPending}
                className="bg-ink hover:bg-ink/90 flex h-[38px] items-center justify-center rounded-xl px-5 text-[13px] font-bold text-white shadow-sm transition-all disabled:opacity-50"
              >
                {create.isPending ? 'Creating...' : 'Create supplier'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 border-b px-5 py-4">
          <h3 className="text-ink text-[14px] font-bold">Suppliers list</h3>
        </div>
        {suppliers.isLoading ? (
          <div className="flex justify-center p-8">
            <Spinner size="sm" />
          </div>
        ) : (suppliers.data ?? []).length === 0 ? (
          <div className="p-8">
            <EmptyState title="No suppliers" description="Add your first supplier above." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">GSTIN</th>
                  <th className="px-5 py-4">Terms</th>
                  <th className="px-5 py-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {(suppliers.data ?? []).map((s: Supplier) => (
                  <tr key={s.id} className="hover:bg-surface-subtle/30 group transition-colors">
                    <td className="text-ink px-5 py-4 font-semibold">{s.name}</td>
                    <td className="text-ink-subtle px-5 py-4 text-xs">
                      {s.email ?? s.phone ?? '—'}
                    </td>
                    <td className="px-5 py-4 font-mono text-xs">{s.gstin ?? '—'}</td>
                    <td className="text-ink-subtle px-5 py-4">{s.paymentTermsDays}d</td>
                    <td className="px-5 py-4">
                      <Badge
                        tone={
                          s.status === 'ACTIVE'
                            ? 'success'
                            : s.status === 'BLOCKED'
                              ? 'danger'
                              : 'neutral'
                        }
                      >
                        {s.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PurchaseOrdersTab() {
  const qc = useQueryClient();
  const pos = useQuery({ queryKey: ['erp-pos'], queryFn: () => api.erp.listPOs() });
  const [expanded, setExpanded] = useState<string | null>(null);
  const detail = useQuery<PurchaseOrderDetail>({
    queryKey: ['erp-po', expanded],
    queryFn: () => api.erp.getPO(expanded!),
    enabled: !!expanded,
  });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'submit' | 'approve' | 'cancel' }) =>
      action === 'submit'
        ? api.erp.submitPO(id)
        : action === 'approve'
          ? api.erp.approvePO(id)
          : api.erp.cancelPO(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['erp-pos'] }),
  });

  if (pos.isLoading)
    return (
      <div className="flex justify-center p-8">
        <Spinner size="lg" />
      </div>
    );
  if ((pos.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-8 shadow-sm">
        <EmptyState
          title="No purchase orders"
          description="POs created via API will appear here."
        />
      </div>
    );

  return (
    <div className="space-y-4">
      {(pos.data ?? []).map((po: PurchaseOrder) => (
        <div key={po.id} className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <span className="text-ink font-mono text-[14px] font-bold">{po.poNumber}</span>
              <span className="text-ink-subtle ml-3 text-[13px] font-medium">
                {formatPaise(po.grandTotalPaise)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Badge tone={PO_STATUS_TONE[po.status] ?? 'neutral'}>
                {po.status.replace(/_/g, ' ')}
              </Badge>
              {po.status === 'DRAFT' && (
                <button
                  className="border-border/60 bg-surface hover:bg-surface-subtle/50 text-ink ml-2 rounded-xl border px-3 py-1.5 text-[12px] font-semibold transition-colors"
                  onClick={() => act.mutate({ id: po.id, action: 'submit' })}
                >
                  Submit
                </button>
              )}
              {po.status === 'SUBMITTED' && (
                <button
                  className="bg-ink hover:bg-ink/90 ml-2 rounded-xl px-3 py-1.5 text-[12px] font-semibold text-white transition-colors"
                  onClick={() => act.mutate({ id: po.id, action: 'approve' })}
                >
                  Approve
                </button>
              )}
              {!['CANCELLED', 'CLOSED', 'RECEIVED'].includes(po.status) && (
                <button
                  className="border-border/60 text-ink-subtle hover:bg-surface-subtle/50 hover:text-ink ml-1 rounded-xl border px-3 py-1.5 text-[12px] font-medium transition-colors"
                  onClick={() => act.mutate({ id: po.id, action: 'cancel' })}
                >
                  Cancel
                </button>
              )}
              <button
                className="text-ink-subtle hover:bg-surface-subtle/50 hover:text-ink ml-1 rounded-xl px-3 py-1.5 text-[12px] font-medium transition-colors"
                onClick={() => setExpanded(expanded === po.id ? null : po.id)}
              >
                {expanded === po.id ? 'Hide' : 'Items'}
              </button>
            </div>
          </div>
          {expanded === po.id && (
            <div className="border-border/60 mt-4 overflow-hidden rounded-xl border">
              {detail.isLoading ? (
                <div className="flex justify-center p-4">
                  <Spinner size="sm" />
                </div>
              ) : detail.data ? (
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                      <th className="px-4 py-3">Description</th>
                      <th className="px-4 py-3">Qty</th>
                      <th className="px-4 py-3">Unit cost</th>
                      <th className="px-4 py-3">Received</th>
                      <th className="px-4 py-3">Line total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-border/40 divide-y text-[13px]">
                    {detail.data.items.map((it) => (
                      <tr key={it.id} className="hover:bg-surface-subtle/30 transition-colors">
                        <td className="text-ink px-4 py-3 font-medium">{it.description}</td>
                        <td className="text-ink-subtle px-4 py-3">{it.qty}</td>
                        <td className="px-4 py-3 font-mono">{formatPaise(it.unitCostPaise)}</td>
                        <td className="text-ink-subtle px-4 py-3">
                          {it.receivedQty}/{it.qty}
                        </td>
                        <td className="px-4 py-3 font-mono font-semibold">
                          {formatPaise(it.lineTotalPaise)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function InventoryTab() {
  const warehouses = useQuery({
    queryKey: ['erp-warehouses'],
    queryFn: () => api.erp.listWarehouses(),
  });
  const batches = useQuery({ queryKey: ['erp-batches'], queryFn: () => api.erp.listBatches() });

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-ink mb-4 text-[14px] font-bold">Warehouses</h3>
        {warehouses.isLoading ? (
          <div className="flex justify-center p-8">
            <Spinner size="sm" />
          </div>
        ) : (warehouses.data ?? []).length === 0 ? (
          <p className="text-ink-subtle text-[13px]">No warehouses.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(warehouses.data ?? []).map((w: InternalWarehouse) => (
              <div
                key={w.id}
                className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-ink font-mono text-[13px] font-bold">{w.code}</span>
                  {w.isDefault && <Badge tone="success">Default</Badge>}
                </div>
                <p className="text-ink text-[14px] font-medium">{w.name}</p>
                <p className="text-ink-subtle mt-1 text-[12px]">
                  {w.city}, {w.stateCode} · {w.pincode}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 border-b px-5 py-4">
          <h3 className="text-ink text-[14px] font-bold">Inventory batches</h3>
        </div>
        {batches.isLoading ? (
          <div className="flex justify-center p-8">
            <Spinner size="sm" />
          </div>
        ) : (batches.data ?? []).length === 0 ? (
          <div className="p-8">
            <EmptyState title="No batches on hand" description="Receive a PO to get batches." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-5 py-4">Batch</th>
                  <th className="px-5 py-4">Item</th>
                  <th className="px-5 py-4">On hand</th>
                  <th className="px-5 py-4">Unit cost</th>
                  <th className="px-5 py-4">Value</th>
                  <th className="px-5 py-4">Expiry</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {(batches.data ?? []).map((b: InventoryBatch) => (
                  <tr key={b.id} className="hover:bg-surface-subtle/30 transition-colors">
                    <td className="text-ink px-5 py-4 font-mono font-medium">{b.batchCode}</td>
                    <td className="text-ink px-5 py-4 font-medium">{b.itemDescription}</td>
                    <td className="text-ink-subtle px-5 py-4">
                      {b.qtyOnHand}/{b.qtyReceived}
                    </td>
                    <td className="px-5 py-4 font-mono">{formatPaise(b.unitCostPaise)}</td>
                    <td className="text-ink px-5 py-4 font-mono font-semibold">
                      {formatPaise(b.qtyOnHand * b.unitCostPaise)}
                    </td>
                    <td className="text-ink-subtle px-5 py-4 text-[12px]">
                      {b.expiryAt ? new Date(b.expiryAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function BillsTab() {
  const qc = useQueryClient();
  const bills = useQuery({ queryKey: ['erp-bills'], queryFn: () => api.erp.listBills() });

  const approve = useMutation({
    mutationFn: (id: string) => api.erp.approveBill(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['erp-bills'] }),
  });
  const pay = useMutation({
    mutationFn: (id: string) => api.erp.payBill(id, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['erp-bills'] }),
  });

  if (bills.isLoading)
    return (
      <div className="flex justify-center p-8">
        <Spinner size="lg" />
      </div>
    );
  if ((bills.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-8 shadow-sm">
        <EmptyState
          title="No supplier bills"
          description="Bills created via API will appear here."
        />
      </div>
    );

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
              <th className="px-5 py-4">Bill #</th>
              <th className="px-5 py-4">Supplier inv.</th>
              <th className="px-5 py-4">Total</th>
              <th className="px-5 py-4">Paid</th>
              <th className="px-5 py-4">Due</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-border/40 divide-y text-[13px]">
            {(bills.data ?? []).map((b: SupplierBill) => (
              <tr key={b.id} className="hover:bg-surface-subtle/30 transition-colors">
                <td className="text-ink px-5 py-4 font-mono font-medium">{b.billNumber}</td>
                <td className="px-5 py-4">{b.supplierInvoiceNo}</td>
                <td className="text-ink px-5 py-4 font-mono font-semibold">
                  {formatPaise(b.totalPaise)}
                </td>
                <td className="px-5 py-4 font-mono">{formatPaise(b.paidPaise)}</td>
                <td className="text-ink-subtle px-5 py-4 text-[12px]">
                  {new Date(b.dueAt).toLocaleDateString()}
                </td>
                <td className="px-5 py-4">
                  <Badge tone={BILL_STATUS_TONE[b.status] ?? 'neutral'}>
                    {b.status.replace(/_/g, ' ')}
                  </Badge>
                </td>
                <td className="px-5 py-4">
                  <div className="flex gap-2">
                    {b.status === 'PENDING' && (
                      <button
                        className="bg-ink hover:bg-ink/90 rounded-xl px-3 py-1.5 text-[12px] font-semibold text-white transition-colors"
                        onClick={() => approve.mutate(b.id)}
                      >
                        Approve
                      </button>
                    )}
                    {(b.status === 'APPROVED' || b.status === 'PARTIALLY_PAID') && (
                      <button
                        className="border-border/60 bg-surface hover:bg-surface-subtle/50 text-ink rounded-xl border px-3 py-1.5 text-[12px] font-semibold transition-colors"
                        onClick={() => pay.mutate(b.id)}
                      >
                        Pay
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExpensesTab() {
  const qc = useQueryClient();
  const expenses = useQuery({ queryKey: ['erp-expenses'], queryFn: () => api.erp.listExpenses() });
  const [form, setForm] = useState({
    category: 'OFFICE' as (typeof EXPENSE_CATEGORIES)[number],
    description: '',
    amountRupees: '',
    payeeName: '',
  });

  const submit = useMutation({
    mutationFn: () =>
      api.erp.submitExpense({
        category: form.category,
        description: form.description.trim(),
        amountPaise: Math.round(Number(form.amountRupees) * 100),
        ...(form.payeeName ? { payeeName: form.payeeName } : {}),
        incurredAt: new Date().toISOString(),
      }),
    onSuccess: () => {
      setForm({ category: 'OFFICE', description: '', amountRupees: '', payeeName: '' });
      void qc.invalidateQueries({ queryKey: ['erp-expenses'] });
    },
  });
  const approve = useMutation({
    mutationFn: (id: string) => api.erp.approveExpense(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['erp-expenses'] }),
  });
  const reject = useMutation({
    mutationFn: (id: string) => api.erp.rejectExpense(id, 'Not approved'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['erp-expenses'] }),
  });
  const pay = useMutation({
    mutationFn: (id: string) => api.erp.payExpense(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['erp-expenses'] }),
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 border-b px-5 py-4">
          <h3 className="text-ink text-[14px] font-bold">Submit expense</h3>
        </div>
        <div className="p-5">
          <form
            className="grid gap-4 sm:grid-cols-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (form.description.length >= 2 && Number(form.amountRupees) > 0) submit.mutate();
            }}
          >
            <div>
              <label className="text-ink mb-1 block text-sm font-medium">Category</label>
              <select
                className="border-border/60 focus:border-ink focus:ring-ink/20 bg-surface h-10 w-full rounded-xl border px-3 py-2 text-[13px] transition-colors"
                value={form.category}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    category: e.target.value as (typeof EXPENSE_CATEGORIES)[number],
                  }))
                }
              >
                {EXPENSE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              required
            />
            <Input
              label="Amount (₹)"
              type="number"
              min="0"
              step="0.01"
              value={form.amountRupees}
              onChange={(e) => setForm((f) => ({ ...f, amountRupees: e.target.value }))}
              required
            />
            <Input
              label="Payee"
              value={form.payeeName}
              onChange={(e) => setForm((f) => ({ ...f, payeeName: e.target.value }))}
            />
            <div className="border-border/60 flex justify-end border-t pt-4 sm:col-span-4">
              <button
                type="submit"
                disabled={submit.isPending}
                className="bg-ink hover:bg-ink/90 flex h-[38px] items-center justify-center rounded-xl px-5 text-[13px] font-bold text-white shadow-sm transition-all disabled:opacity-50"
              >
                {submit.isPending ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {expenses.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner size="lg" />
        </div>
      ) : (expenses.data ?? []).length === 0 ? (
        <div className="border-border/60 bg-surface rounded-2xl border p-8 shadow-sm">
          <EmptyState title="No expenses" description="Submit an expense above." />
        </div>
      ) : (
        <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-5 py-4">Ref</th>
                  <th className="px-5 py-4">Category</th>
                  <th className="px-5 py-4">Description</th>
                  <th className="px-5 py-4">Total</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {(expenses.data ?? []).map((ex: Expense) => (
                  <tr key={ex.id} className="hover:bg-surface-subtle/30 transition-colors">
                    <td className="text-ink px-5 py-4 font-mono font-medium">{ex.refNumber}</td>
                    <td className="px-5 py-4 text-[12px]">{ex.category.replace(/_/g, ' ')}</td>
                    <td className="px-5 py-4">{ex.description}</td>
                    <td className="text-ink px-5 py-4 font-mono font-semibold">
                      {formatPaise(ex.totalPaise)}
                    </td>
                    <td className="px-5 py-4">
                      <Badge tone={EXPENSE_STATUS_TONE[ex.status] ?? 'neutral'}>{ex.status}</Badge>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-2">
                        {ex.status === 'SUBMITTED' && (
                          <>
                            <button
                              className="bg-ink hover:bg-ink/90 rounded-xl px-3 py-1.5 text-[12px] font-semibold text-white transition-colors"
                              onClick={() => approve.mutate(ex.id)}
                            >
                              Approve
                            </button>
                            <button
                              className="border-border/60 bg-surface hover:bg-surface-subtle/50 text-ink-subtle hover:text-ink rounded-xl border px-3 py-1.5 text-[12px] font-medium transition-colors"
                              onClick={() => reject.mutate(ex.id)}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {ex.status === 'APPROVED' && (
                          <button
                            className="bg-ink hover:bg-ink/90 rounded-xl px-3 py-1.5 text-[12px] font-semibold text-white transition-colors"
                            onClick={() => pay.mutate(ex.id)}
                          >
                            Pay
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export function ErpPage() {
  const [tab, setTab] = useState('Suppliers');
  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">ERP</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Suppliers, purchase orders, inventory, supplier bills and expenses.
          </p>
        </div>
      </div>
      <Tabs
        tabs={['Suppliers', 'Purchase Orders', 'Inventory', 'Bills', 'Expenses']}
        active={tab}
        onChange={setTab}
      />
      <div>
        {tab === 'Suppliers' && <SuppliersTab />}
        {tab === 'Purchase Orders' && <PurchaseOrdersTab />}
        {tab === 'Inventory' && <InventoryTab />}
        {tab === 'Bills' && <BillsTab />}
        {tab === 'Expenses' && <ExpensesTab />}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// POS  (Phase 2.5 — branches, terminals, shifts, sales)
// ══════════════════════════════════════════════════════════════════

const SHIFT_STATUS_TONE: Record<string, BadgeTone> = {
  OPEN: 'warning',
  CLOSED: 'neutral',
  RECONCILED: 'success',
};
const SALE_STATUS_TONE: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  COMPLETED: 'success',
  PARTIALLY_REFUNDED: 'warning',
  REFUNDED: 'danger',
  VOIDED: 'danger',
};

function BranchesTab() {
  const qc = useQueryClient();
  const branches = useQuery({ queryKey: ['pos-branches'], queryFn: () => api.pos.listBranches() });
  const warehouses = useQuery({
    queryKey: ['erp-warehouses'],
    queryFn: () => api.erp.listWarehouses(),
  });
  const [branchForm, setBranchForm] = useState({
    code: '',
    name: '',
    warehouseId: '',
    stateCode: '',
    gstin: '',
  });
  const [termForm, setTermForm] = useState({ branchId: '', code: '', label: '' });
  const [expanded, setExpanded] = useState<string | null>(null);

  const terminals = useQuery({
    queryKey: ['pos-terminals', expanded],
    queryFn: () => api.pos.listTerminals(expanded!),
    enabled: !!expanded,
  });

  const createBranch = useMutation({
    mutationFn: () =>
      api.pos.createBranch({
        code: branchForm.code.toUpperCase(),
        name: branchForm.name.trim(),
        warehouseId: branchForm.warehouseId,
        stateCode: branchForm.stateCode,
        ...(branchForm.gstin ? { gstin: branchForm.gstin.toUpperCase() } : {}),
      }),
    onSuccess: () => {
      setBranchForm({ code: '', name: '', warehouseId: '', stateCode: '', gstin: '' });
      void qc.invalidateQueries({ queryKey: ['pos-branches'] });
    },
  });
  const createTerminal = useMutation({
    mutationFn: () =>
      api.pos.createTerminal({
        branchId: termForm.branchId,
        code: termForm.code.toUpperCase(),
        label: termForm.label.trim(),
      }),
    onSuccess: () => {
      setTermForm({ branchId: '', code: '', label: '' });
      void qc.invalidateQueries({ queryKey: ['pos-terminals'] });
    },
  });
  const retire = useMutation({
    mutationFn: (id: string) => api.pos.retireTerminal(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['pos-terminals'] }),
  });

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
          <div className="border-border/60 border-b px-5 py-4">
            <h3 className="text-ink text-[14px] font-bold">New branch</h3>
          </div>
          <div className="p-5">
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (
                  branchForm.code &&
                  branchForm.name &&
                  branchForm.warehouseId &&
                  branchForm.stateCode
                )
                  createBranch.mutate();
              }}
            >
              <Input
                label="Code"
                value={branchForm.code}
                onChange={(e) =>
                  setBranchForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))
                }
                required
              />
              <Input
                label="Name"
                value={branchForm.name}
                onChange={(e) => setBranchForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
              <div>
                <label className="text-ink mb-1 block text-sm font-medium">Warehouse</label>
                <select
                  className="border-border/60 focus:border-ink focus:ring-ink/20 bg-surface h-10 w-full rounded-xl border px-3 py-2 text-[13px] transition-colors"
                  value={branchForm.warehouseId}
                  onChange={(e) => setBranchForm((f) => ({ ...f, warehouseId: e.target.value }))}
                  required
                >
                  <option value="">Select…</option>
                  {(warehouses.data ?? []).map((w: any) => (
                    <option key={w.id} value={w.id}>
                      {w.code} — {w.name}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="State code"
                value={branchForm.stateCode}
                onChange={(e) => setBranchForm((f) => ({ ...f, stateCode: e.target.value }))}
                required
              />
              <Input
                label="GSTIN"
                value={branchForm.gstin}
                onChange={(e) =>
                  setBranchForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))
                }
              />
              <div className="border-border/60 flex justify-end border-t pt-4">
                <button
                  type="submit"
                  disabled={createBranch.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-[38px] items-center justify-center rounded-xl px-5 text-[13px] font-bold text-white shadow-sm transition-all disabled:opacity-50"
                >
                  {createBranch.isPending ? 'Creating...' : 'Create branch'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
          <div className="border-border/60 border-b px-5 py-4">
            <h3 className="text-ink text-[14px] font-bold">New terminal</h3>
          </div>
          <div className="p-5">
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (termForm.branchId && termForm.code && termForm.label) createTerminal.mutate();
              }}
            >
              <div>
                <label className="text-ink mb-1 block text-sm font-medium">Branch</label>
                <select
                  className="border-border/60 focus:border-ink focus:ring-ink/20 bg-surface h-10 w-full rounded-xl border px-3 py-2 text-[13px] transition-colors"
                  value={termForm.branchId}
                  onChange={(e) => setTermForm((f) => ({ ...f, branchId: e.target.value }))}
                  required
                >
                  <option value="">Select…</option>
                  {(branches.data ?? []).map((b: any) => (
                    <option key={b.id} value={b.id}>
                      {b.code} — {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Code"
                value={termForm.code}
                onChange={(e) => setTermForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                required
              />
              <Input
                label="Label"
                value={termForm.label}
                onChange={(e) => setTermForm((f) => ({ ...f, label: e.target.value }))}
                required
              />
              <div className="border-border/60 flex justify-end border-t pt-4">
                <button
                  type="submit"
                  disabled={createTerminal.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-[38px] items-center justify-center rounded-xl px-5 text-[13px] font-bold text-white shadow-sm transition-all disabled:opacity-50"
                >
                  {createTerminal.isPending ? 'Creating...' : 'Create terminal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {branches.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner size="lg" />
        </div>
      ) : (branches.data ?? []).length === 0 ? (
        <div className="border-border/60 bg-surface rounded-2xl border p-8 shadow-sm">
          <EmptyState title="No branches" description="Create your first POS branch above." />
        </div>
      ) : (
        <div className="space-y-4">
          {(branches.data ?? []).map((b: PosBranch) => (
            <div
              key={b.id}
              className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <span className="text-ink font-mono text-[14px] font-bold">{b.code}</span>
                  <span className="text-ink ml-3 text-[14px] font-medium">{b.name}</span>
                  <span className="text-ink-subtle ml-3 text-[12px]">
                    {b.stateCode} · inv {b.invoicePrefix}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {b.active ? (
                    <Badge tone="success">Active</Badge>
                  ) : (
                    <Badge tone="neutral">Inactive</Badge>
                  )}
                  <button
                    className="text-ink-subtle hover:bg-surface-subtle/50 hover:text-ink ml-2 rounded-xl px-3 py-1.5 text-[12px] font-medium transition-colors"
                    onClick={() => setExpanded(expanded === b.id ? null : b.id)}
                  >
                    {expanded === b.id ? 'Hide' : 'Terminals'}
                  </button>
                </div>
              </div>
              {expanded === b.id && (
                <div className="border-border/60 mt-4 overflow-hidden rounded-xl border">
                  {terminals.isLoading ? (
                    <div className="flex justify-center p-4">
                      <Spinner size="sm" />
                    </div>
                  ) : (terminals.data ?? []).length === 0 ? (
                    <div className="text-ink-subtle p-4 text-center text-[13px]">No terminals.</div>
                  ) : (
                    <ul className="divide-border/40 divide-y">
                      {(terminals.data ?? []).map((t: any) => (
                        <li
                          key={t.id}
                          className="bg-surface-subtle/30 hover:bg-surface-subtle/50 flex items-center justify-between px-4 py-3 text-[13px] transition-colors"
                        >
                          <span className="text-ink">
                            <span className="font-mono font-bold">{t.code}</span> — {t.label}
                          </span>
                          <div className="flex items-center gap-2">
                            <Badge tone={t.status === 'ACTIVE' ? 'success' : 'neutral'}>
                              {t.status}
                            </Badge>
                            {t.status === 'ACTIVE' && (
                              <button
                                className="border-border/60 text-ink-subtle hover:bg-surface-subtle/50 hover:text-ink rounded-xl border px-3 py-1 text-[12px] font-medium transition-colors"
                                onClick={() => retire.mutate(t.id)}
                              >
                                Retire
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ShiftsTab() {
  const shifts = useQuery({ queryKey: ['pos-shifts'], queryFn: () => api.pos.listShifts() });

  if (shifts.isLoading)
    return (
      <div className="flex justify-center p-8">
        <Spinner size="lg" />
      </div>
    );
  if ((shifts.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-8 shadow-sm">
        <EmptyState title="No shifts" description="Cashier shifts will appear here." />
      </div>
    );

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
              <th className="px-5 py-4">Shift #</th>
              <th className="px-5 py-4">Opened</th>
              <th className="px-5 py-4">Closed</th>
              <th className="px-5 py-4">Opening float</th>
              <th className="px-5 py-4">Counted</th>
              <th className="px-5 py-4">Variance</th>
              <th className="px-5 py-4">Status</th>
            </tr>
          </thead>
          <tbody className="divide-border/40 divide-y text-[13px]">
            {(shifts.data ?? []).map((s: PosShift) => (
              <tr key={s.id} className="hover:bg-surface-subtle/30 transition-colors">
                <td className="text-ink px-5 py-4 font-mono font-medium">{s.shiftNumber}</td>
                <td className="px-5 py-4 text-[12px]">{new Date(s.openedAt).toLocaleString()}</td>
                <td className="px-5 py-4 text-[12px]">
                  {s.closedAt ? new Date(s.closedAt).toLocaleString() : '—'}
                </td>
                <td className="px-5 py-4 font-mono">{formatPaise(s.openingFloatPaise)}</td>
                <td className="px-5 py-4 font-mono">
                  {s.closingCashCountedPaise != null ? formatPaise(s.closingCashCountedPaise) : '—'}
                </td>
                <td
                  className={`px-5 py-4 font-mono ${s.variancePaise && s.variancePaise !== 0 ? 'text-red-600' : ''}`}
                >
                  {s.variancePaise != null ? formatPaise(s.variancePaise) : '—'}
                </td>
                <td className="px-5 py-4">
                  <Badge tone={SHIFT_STATUS_TONE[s.status] ?? 'neutral'}>{s.status}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SalesTab() {
  const branches = useQuery({ queryKey: ['pos-branches'], queryFn: () => api.pos.listBranches() });
  const [branchId, setBranchId] = useState('');
  const sales = useQuery({
    queryKey: ['pos-branch-sales', branchId],
    queryFn: () => api.pos.branchSales(branchId),
    enabled: !!branchId,
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm">
        <label className="text-ink mb-1 block text-sm font-medium">Branch</label>
        <select
          className="border-border/60 focus:border-ink focus:ring-ink/20 bg-surface h-10 w-full rounded-xl border px-3 py-2 text-[13px] transition-colors sm:w-72"
          value={branchId}
          onChange={(e) => setBranchId(e.target.value)}
        >
          <option value="">Select a branch…</option>
          {(branches.data ?? []).map((b: any) => (
            <option key={b.id} value={b.id}>
              {b.code} — {b.name}
            </option>
          ))}
        </select>
      </div>

      {!branchId ? (
        <div className="border-border/60 bg-surface rounded-2xl border p-8 shadow-sm">
          <p className="text-ink-subtle text-center text-[13px]">
            Pick a branch to view its sales.
          </p>
        </div>
      ) : sales.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner size="lg" />
        </div>
      ) : (sales.data ?? []).length === 0 ? (
        <div className="border-border/60 bg-surface rounded-2xl border p-8 shadow-sm">
          <EmptyState title="No sales" description="No sales recorded for this branch." />
        </div>
      ) : (
        <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-5 py-4">Invoice</th>
                  <th className="px-5 py-4">When</th>
                  <th className="px-5 py-4">Customer</th>
                  <th className="px-5 py-4">Total</th>
                  <th className="px-5 py-4">Refunded</th>
                  <th className="px-5 py-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {(sales.data ?? []).map((s: PosSale) => (
                  <tr key={s.id} className="hover:bg-surface-subtle/30 transition-colors">
                    <td className="text-ink px-5 py-4 font-mono font-medium">{s.invoiceNumber}</td>
                    <td className="px-5 py-4 text-[12px]">
                      {new Date(s.createdAt).toLocaleString()}
                    </td>
                    <td className="px-5 py-4">{s.customerName ?? 'Walk-in'}</td>
                    <td className="text-ink px-5 py-4 font-mono font-semibold">
                      {formatPaise(s.grandTotalPaise)}
                    </td>
                    <td className="px-5 py-4 font-mono">
                      {s.refundedPaise > 0 ? formatPaise(s.refundedPaise) : '—'}
                    </td>
                    <td className="px-5 py-4">
                      <Badge tone={SALE_STATUS_TONE[s.status] ?? 'neutral'}>
                        {s.status.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export function PosPage() {
  const [tab, setTab] = useState('Branches');
  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">POS</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Retail branches, terminals, cashier shifts and in-store sales.
          </p>
        </div>
      </div>
      <Tabs tabs={['Branches', 'Shifts', 'Sales']} active={tab} onChange={setTab} />
      <div>
        {tab === 'Branches' && <BranchesTab />}
        {tab === 'Shifts' && <ShiftsTab />}
        {tab === 'Sales' && <SalesTab />}
      </div>
    </div>
  );
}
